import './CountChart.scss';

import { useCallback, useEffect, useRef } from 'react';
import { Typography } from '@trackman/web-shared-components';
import { useChartDataDisplay } from 'hooks/helperHooks';

export const counts = ['0-0', '0-1', '0-2', '1-0', '1-1', '1-2', '2-0', '2-1', '2-2', '3-0', '3-1', '3-2'] as const;
export type Count = (typeof counts)[number];

export interface Slice {
  value: number;
  color: string;
}

export type CountChartData = Map<Count, Slice[]>;

export interface CountChartProps {
  chartData: CountChartData;
  isLoading: boolean;
  colorsToSimulate: string[];
}

const countNeighborsMap = new Map<Count, number[]>([
  ['0-0', [135, 45]],
  ['0-1', [315, 135, 45]],
  ['1-0', [225, 135, 45]],
  ['0-2', [315, 45]],
  ['1-1', [225, 315, 135, 45]],
  ['2-0', [225, 135, 45]],
  ['1-2', [225, 315, 45]],
  ['2-1', [225, 315, 135, 45]],
  ['3-0', [225, 135]],
  ['2-2', [225, 315, 45]],
  ['3-1', [225, 315, 135]],
  ['3-2', [225, 315]],
]);

const degreeToRadian = 0.01745;
const pythagorasConstant = Math.sqrt(2);

const renderCountChart = (canvasRef: React.RefObject<HTMLCanvasElement>, size: number, count: Count, data: Slice[]) => {
  const canvas = canvasRef?.current;
  if (!canvas) {
    return;
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }

  ctx.clearRect(0, 0, size, size);

  const radius = size * 0.5;
  const center = size * 0.5;
  const counterClockwise = false;
  const startAngle = -90 * degreeToRadian;
  let currentAngle = startAngle;

  const totalValue = data.reduce((sum, item) => sum + item.value, 0);

  // Draw connections to other counts
  const centerToCornerDistance = radius * pythagorasConstant;
  const countNeighbors = countNeighborsMap.get(count);
  if (countNeighbors) {
    countNeighbors.forEach((angle) => {
      ctx.beginPath();
      ctx.moveTo(center, center);
      ctx.lineTo(
        center + Math.cos(angle * degreeToRadian) * centerToCornerDistance,
        center + Math.sin(angle * degreeToRadian) * centerToCornerDistance
      );
      ctx.strokeStyle = '#7B7B7B';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.closePath();
    });
  }

  // Empty circle
  if (totalValue === 0) {
    const strokeWidth = 3;

    ctx.beginPath();
    ctx.arc(center, center, radius - strokeWidth, 0, 2 * Math.PI);
    ctx.strokeStyle = '#414141';
    ctx.lineWidth = strokeWidth;
    ctx.stroke();
    ctx.fillStyle = 'white';
    ctx.fill();
    ctx.closePath();
    return;
  }

  // Draw pie slices
  data.forEach((slice) => {
    const percentage = totalValue > 0 ? slice.value / totalValue : 0;
    const endAngle = currentAngle + Math.PI * 2 * percentage;

    ctx.beginPath();
    ctx.arc(center, center, radius, currentAngle, endAngle, counterClockwise);
    ctx.lineTo(center, center);
    ctx.fillStyle = slice.color;
    ctx.fill();
    ctx.closePath();

    currentAngle = endAngle;
  });

  // Draw pie slice separators
  if (data.filter((item) => item.value > 0).length > 1) {
    currentAngle = startAngle;

    data.forEach((slice) => {
      const percentage = totalValue > 0 ? slice.value / totalValue : 0;
      const endAngle = currentAngle + Math.PI * 2 * percentage;

      ctx.beginPath();
      ctx.moveTo(center, center);
      ctx.lineTo(center + radius * Math.cos(currentAngle), center + radius * Math.sin(currentAngle));
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.closePath();

      currentAngle = endAngle;
    });
  }
};

const CountChart = ({ chartData, isLoading, colorsToSimulate }: CountChartProps) => {
  const countChartSize = 64; // Drawing resolution since actual size is responsive

  const canvasRefs = useRef<React.RefObject<HTMLCanvasElement>[]>(
    Array(counts.length)
      .fill(null)
      .map(() => useRef<HTMLCanvasElement>(null))
  );

  const simulateChartData = useCallback(
    (isLoading: boolean): CountChartData => {
      if (isLoading) {
        const newChartData: CountChartData = new Map();
        counts.forEach((count) => {
          const simulatedSlices = colorsToSimulate.map((color) => ({
            color,
            value: Math.floor(Math.random() * 100),
          }));
          newChartData.set(count, simulatedSlices);
        });
        return newChartData;
      } else {
        return chartData;
      }
    },
    [chartData]
  );

  const chartDataDisplay = useChartDataDisplay(chartData, simulateChartData, isLoading);

  useEffect(() => {
    counts.forEach((count, index) => {
      renderCountChart(canvasRefs.current[index], countChartSize, count, chartDataDisplay.get(count) ?? []);
    });
  }, [chartDataDisplay]);

  const gridItemStyle = (count: Count): React.CSSProperties => {
    return { gridArea: `_${count}_` };
  };

  return (
    <div className='CountChart-grid'>
      {counts.map((count, index) => (
        <div key={count} className='CountChart-grid-item' style={gridItemStyle(count)}>
          <Typography className='CountChart-grid-item-header' component='div'>
            {count}
          </Typography>
          <canvas ref={canvasRefs.current[index]} height={countChartSize} width={countChartSize} />
        </div>
      ))}
    </div>
  );
};

export default CountChart;
